import { PlayerAttributes, CalculatedRatings } from '../types';

/**
 * Check if all required attributes are present (not null)
 */
export function hasCompleteRatings(attributes: PlayerAttributes | null): boolean {
  if (!attributes) return false;

  const allAttributes = [
    attributes.passing,
    attributes.first_touch,
    attributes.dribbling,
    attributes.finishing,
    attributes.pace,
    attributes.stamina,
    attributes.agility,
    attributes.acceleration,
    attributes.balance_strength,
    attributes.positioning,
    attributes.decision_making,
    attributes.work_rate,
    attributes.composure,
    attributes.tackling,
    attributes.marking,
    attributes.anticipation,
    attributes.leadership,
    attributes.off_the_ball,
    attributes.teamwork,
    attributes.goalkeeping,
  ];

  return allAttributes.every((attr) => attr !== null && attr >= 1 && attr <= 10);
}

/**
 * Calculate position-specific ratings based on weighted attribute formulas
 */
export function calculatePositionRatings(
  attributes: PlayerAttributes
): CalculatedRatings | null {
  if (!hasCompleteRatings(attributes)) {
    return null;
  }

  // Safe to assert non-null since hasCompleteRatings passed
  const a = attributes as Required<PlayerAttributes>;

  // Forward Rating
  const fwd_rating =
    a.finishing * 2.0 +
    a.off_the_ball * 2.0 +
    a.pace * 1.5 +
    a.positioning * 1.5 +
    a.acceleration * 1.5 +
    a.dribbling * 1.2 +
    a.anticipation * 1.2 +
    a.composure * 1.0 +
    a.first_touch * 1.0 +
    a.balance_strength * 1.0 +
    a.teamwork * 0.8 +
    a.agility * 0.8;

  // Midfielder Rating
  const mid_rating =
    a.passing * 2.0 +
    a.stamina * 1.5 +
    a.decision_making * 1.5 +
    a.off_the_ball * 1.5 +
    a.teamwork * 1.5 +
    a.work_rate * 1.2 +
    a.first_touch * 1.2 +
    a.anticipation * 1.2 +
    a.positioning * 1.0 +
    a.agility * 1.0 +
    a.leadership * 1.0 +
    a.acceleration * 1.0 +
    a.balance_strength * 1.0 +
    a.dribbling * 0.8;

  // Defender Rating
  const def_rating =
    a.tackling * 2.0 +
    a.marking * 2.0 +
    a.positioning * 1.5 +
    a.anticipation * 1.5 +
    a.teamwork * 1.5 +
    a.composure * 1.2 +
    a.pace * 1.2 +
    a.balance_strength * 1.2 +
    a.work_rate * 1.0 +
    a.stamina * 1.0 +
    a.acceleration * 1.0 +
    a.leadership * 0.8 +
    a.first_touch * 0.8 +
    a.passing * 0.8;

  // Goalkeeper Rating
  const gk_rating =
    a.goalkeeping * 3.0 +
    a.anticipation * 1.5 +
    a.leadership * 1.2 +
    a.positioning * 1.0 +
    a.composure * 1.0 +
    a.decision_making * 0.8;

  // Determine best position
  const ratings = {
    FWD: fwd_rating,
    MID: mid_rating,
    DEF: def_rating,
    GK: gk_rating,
  };

  const best_position = (Object.entries(ratings).reduce((best, current) =>
    current[1] > best[1] ? current : best
  )[0] as 'FWD' | 'MID' | 'DEF' | 'GK');

  // Calculate versatility (average of non-primary positions)
  const primaryRating = ratings[best_position];
  const otherRatings = Object.values(ratings).filter((r) => r !== primaryRating);
  const versatility = otherRatings.reduce((sum, r) => sum + r, 0) / otherRatings.length;

  // Overall rating (70% primary, 30% versatility)
  const overall_rating = primaryRating * 0.7 + versatility * 0.3;

  return {
    fwd_rating: Math.round(fwd_rating * 10) / 10,
    mid_rating: Math.round(mid_rating * 10) / 10,
    def_rating: Math.round(def_rating * 10) / 10,
    gk_rating: Math.round(gk_rating * 10) / 10,
    best_position,
    overall_rating: Math.round(overall_rating * 10) / 10,
  };
}

/**
 * Validate attribute value
 */
export function isValidRating(value: any): boolean {
  if (value === null || value === undefined || value === '') return true;
  const num = Number(value);
  return !isNaN(num) && num >= 1 && num <= 10 && Number.isInteger(num);
}

/**
 * Get balance quality indicator
 */
export function getBalanceQuality(difference: number): {
  quality: 'EXCELLENT' | 'GOOD' | 'FAIR';
  color: string;
  emoji: string;
} {
  if (difference < 2) {
    return { quality: 'EXCELLENT', color: 'text-green-600', emoji: '🟢' };
  } else if (difference <= 5) {
    return { quality: 'GOOD', color: 'text-yellow-600', emoji: '🟡' };
  } else {
    return { quality: 'FAIR', color: 'text-red-600', emoji: '🔴' };
  }
}
